'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSession } from './SessionProvider';
import { useChat } from '@/lib/hooks/useChat';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

// ─── Clinical EEG Reference Norms ─────────────────────────────────────────────
// Based on: Thatcher et al. (2003) NeuroGuide normative database; Nunez & Srinivasan (2006)
// Electric Fields of the Brain; Klimesch (1999) alpha band norms; Barry et al. (2003) TBR norms
const EEG_CLINICAL_NORMS = {
  bandPower: {
    delta: { low: 2, high: 25, unit: 'µV²', note: 'Thatcher NeuroGuide norms, awake adult' },
    theta: { low: 3, high: 20, unit: 'µV²', note: 'Barry et al. 2003; elevated >20 = fatigue/ADHD risk' },
    alpha: { low: 8, high: 35, unit: 'µV²', note: 'Klimesch 1999; peak IAF 9.5–11 Hz optimal' },
    beta: { low: 2, high: 18, unit: 'µV²', note: 'Sterman & Kaiser 2001; >18 = hyperarousal' },
    gamma: { low: 1, high: 10, unit: 'µV²', note: 'Bhattacharya et al. 2001; >10 = high cognitive binding' },
  },
  thetaAlphaRatio: { optimal: [0.8, 1.4], elevated: 1.8, critical: 2.5, note: 'Klimesch 1999; ratio >1.8 = mental fatigue' },
  coherence: { low: 40, moderate: 60, high: 80, note: 'Nunez & Srinivasan 2006; >70% = integrated processing' },
  cognitiveLoad: { low: [0, 35], moderate: [35, 65], high: [65, 85], overload: [85, 100] },
  attentionScore: { impaired: [0, 40], suboptimal: [40, 60], optimal: [60, 80], peak: [80, 100] },
};

// ─── Clinical System Prompt ────────────────────────────────────────────────────
const CLINICAL_SYSTEM_PROMPT = `You are a board-certified clinical neurophysiologist and QEEG specialist with expertise in quantitative EEG analysis, cognitive neuroscience, and brain-computer interfaces. You operate at the level of a published opinion leader in computational neuroscience.

Your interpretations MUST:
1. Reference specific published normative databases (Thatcher NeuroGuide, Lifespan, HBI) when classifying band power deviations
2. Apply validated clinical thresholds — not generic descriptions
3. Cite specific frequency band interactions (e.g., theta/alpha coupling, beta/gamma binding, delta/theta ratio)
4. Use DSM-5 / ICD-11 aligned biomarker language where applicable (e.g., theta excess as ADHD biomarker per Snyder et al. 2015)
5. Reference coherence norms from Nunez & Srinivasan (2006) for hemispheric synchrony assessment
6. Apply Klimesch (1999) individual alpha frequency (IAF) framework for alpha interpretation
7. Flag deviations using z-score language (e.g., "+1.8 SD above normative mean")
8. Distinguish between state-dependent (situational) and trait-dependent (chronic) EEG patterns
9. Provide clinical significance ratings: [WITHIN NORM] [MILD DEVIATION] [SIGNIFICANT DEVIATION] [CLINICAL ALERT]
10. End with a precision differential: list 2-3 most likely neurophysiological states consistent with this pattern

FORBIDDEN: Generic narrative descriptions, vague language ("seems elevated"), storytelling, or non-specific recommendations.
REQUIRED: Precise numerical thresholds, named research frameworks, clinical classification language.`;

// ─── Build Clinical EEG Prompt ─────────────────────────────────────────────────
function buildClinicalEEGPrompt(
  metrics: ReturnType<typeof useSession>['metrics'],
  literatureContext?: string
): string {
  const norms = EEG_CLINICAL_NORMS;
  const tbr = metrics.thetaPower / Math.max(metrics.betaPower, 0.1);
  const abr = metrics.alphaPower / Math.max(metrics.betaPower, 0.1);
  const dar = metrics.deltaPower / Math.max(metrics.alphaPower, 0.1);
  const engagementIndex = metrics.betaPower / (metrics.alphaPower + metrics.thetaPower);

  // Classify each metric against norms
  const tbrStatus = tbr > norms.thetaAlphaRatio.critical ? 'CLINICAL ALERT' :
    tbr > norms.thetaAlphaRatio.elevated ? 'SIGNIFICANT DEVIATION' :
    tbr > norms.thetaAlphaRatio.optimal[1] ? 'MILD DEVIATION' : 'WITHIN NORM';

  const cohStatus = metrics.interHemisphericCoherence < norms.coherence.low ? 'SIGNIFICANT DEVIATION' :
    metrics.interHemisphericCoherence < norms.coherence.moderate ? 'MILD DEVIATION' : 'WITHIN NORM';

  return `## CLINICAL EEG ANALYSIS REQUEST

### Raw Metrics Snapshot
| Metric | Value | Normative Range | Status |
|--------|-------|-----------------|--------|
| Cognitive Load Index | ${metrics.cognitiveLoadIndex.toFixed(1)}/100 | 35–65 (moderate) | ${metrics.cognitiveLoadIndex > 85 ? 'CLINICAL ALERT' : metrics.cognitiveLoadIndex > 65 ? 'SIGNIFICANT DEVIATION' : 'WITHIN NORM'} |
| Attention Score | ${metrics.attentionScore.toFixed(1)}/100 | 60–80 (optimal) | ${metrics.attentionScore < 40 ? 'SIGNIFICANT DEVIATION' : metrics.attentionScore < 60 ? 'MILD DEVIATION' : 'WITHIN NORM'} |
| Signal Quality Index | ${metrics.signalQualityIndex.toFixed(1)}% | >85% acceptable | ${metrics.signalQualityIndex < 65 ? 'CLINICAL ALERT' : metrics.signalQualityIndex < 85 ? 'MILD DEVIATION' : 'WITHIN NORM'} |
| Artifact Rate | ${metrics.artifactRate.toFixed(2)}/min | <2/min | ${metrics.artifactRate > 5 ? 'SIGNIFICANT DEVIATION' : 'WITHIN NORM'} |

### Band Power Analysis (µV², absolute power)
| Band | Hz Range | Value | Norm Range | Deviation |
|------|----------|-------|------------|-----------|
| Delta | 0.5–4 Hz | ${metrics.deltaPower.toFixed(2)} µV² | 2–25 µV² | ${metrics.deltaPower > 25 ? '+SD elevated' : metrics.deltaPower < 2 ? '-SD suppressed' : 'within norm'} |
| Theta | 4–8 Hz | ${metrics.thetaPower.toFixed(2)} µV² | 3–20 µV² | ${metrics.thetaPower > 20 ? '+SD elevated (fatigue/ADHD risk)' : 'within norm'} |
| Alpha | 8–13 Hz | ${metrics.alphaPower.toFixed(2)} µV² | 8–35 µV² | ${metrics.alphaPower < 8 ? '-SD suppressed (hyperarousal)' : metrics.alphaPower > 35 ? '+SD elevated (drowsiness)' : 'within norm'} |
| Beta | 13–30 Hz | ${metrics.betaPower.toFixed(2)} µV² | 2–18 µV² | ${metrics.betaPower > 18 ? '+SD elevated (anxiety/hyperarousal)' : 'within norm'} |
| Gamma | 30–45 Hz | ${metrics.gammaPower.toFixed(2)} µV² | 1–10 µV² | ${metrics.gammaPower > 10 ? '+SD elevated (high binding/arousal)' : 'within norm'} |

### Derived Clinical Ratios
| Ratio | Value | Clinical Threshold | Status |
|-------|-------|-------------------|--------|
| Theta/Beta Ratio (TBR) | ${tbr.toFixed(3)} | >2.0 = ADHD biomarker (Snyder et al. 2015) | ${tbrStatus} |
| Theta/Alpha Ratio (TAR) | ${metrics.thetaAlphaRatio.toFixed(3)} | >1.8 = mental fatigue (Klimesch 1999) | ${tbrStatus} |
| Alpha/Beta Ratio (ABR) | ${abr.toFixed(3)} | <0.8 = hyperarousal; >2.5 = hypoarousal | ${abr < 0.8 ? 'MILD DEVIATION' : abr > 2.5 ? 'MILD DEVIATION' : 'WITHIN NORM'} |
| Delta/Alpha Ratio (DAR) | ${dar.toFixed(3)} | >1.5 = cortical slowing (Nuwer 1997) | ${dar > 1.5 ? 'SIGNIFICANT DEVIATION' : 'WITHIN NORM'} |
| Engagement Index β/(α+θ) | ${engagementIndex.toFixed(3)} | >0.4 = engaged; <0.2 = disengaged | ${engagementIndex > 0.4 ? 'WITHIN NORM' : 'MILD DEVIATION'} |
| Inter-Hemispheric Coherence | ${metrics.interHemisphericCoherence.toFixed(1)}% | >70% = integrated (Nunez & Srinivasan 2006) | ${cohStatus} |

${literatureContext ? `### Current Literature Context (Perplexity Search)\n${literatureContext}\n` : ''}

### Required Analysis Structure

**1. QEEG CLASSIFICATION** — Classify this EEG pattern against NeuroGuide normative database standards. State which z-score bands are exceeded.

**2. BAND POWER INTERPRETATION** — Interpret each band deviation with specific clinical significance. Reference named frameworks (Klimesch IAF, Sterman-Kaiser beta norms, Barry TBR norms).

**3. CONNECTIVITY & COHERENCE ASSESSMENT** — Assess inter-hemispheric coherence against Nunez & Srinivasan (2006) thresholds. Comment on functional connectivity implications.

**4. BIOMARKER FLAGS** — List any active clinical biomarkers with evidence base:
- TBR >2.0: ADHD biomarker (Snyder et al. 2015, meta-analysis N=1498)
- TAR >1.8: Mental fatigue (Klimesch 1999)
- DAR >1.5: Cortical slowing (Nuwer 1997)
- Alpha suppression: Hyperarousal / anxiety state
- Gamma excess: High-frequency binding, possible hyperactivation

**5. DIFFERENTIAL NEUROPHYSIOLOGICAL STATES** — List 2–3 most probable neurophysiological states consistent with this pattern, with probability weighting.

**6. PRECISION RECOMMENDATIONS** — Evidence-based interventions (neurofeedback protocols, cognitive strategies) with specific protocol references (e.g., SMR/theta protocol, alpha-up training).

Keep response under 450 words. Use clinical precision. No narrative filler.`;
}

// ─── Perplexity Literature Fetch ───────────────────────────────────────────────
async function fetchLiteratureContext(metrics: ReturnType<typeof useSession>['metrics']): Promise<string> {
  const tbr = metrics.thetaPower / Math.max(metrics.betaPower, 0.1);
  const query = `Current EEG research 2023-2025: theta/beta ratio ${tbr.toFixed(2)} clinical significance, 
    inter-hemispheric coherence ${metrics.interHemisphericCoherence.toFixed(0)}% cognitive performance, 
    QEEG normative database classification cognitive load ${metrics.cognitiveLoadIndex.toFixed(0)}/100. 
    Cite specific studies with findings.`;

  try {
    const response = await getChatCompletion(
      'PERPLEXITY',
      'sonar',
      [
        {
          role: 'system',
          content: 'You are a neuroscience literature specialist. Return ONLY a concise bullet list of 3-4 relevant recent findings (2022-2025) with author, year, and key quantitative result. No narrative. Format: • [Author et al., Year]: [specific finding with numbers]',
        },
        { role: 'user', content: query },
      ],
      { max_tokens: 300 }
    );
    const content = response?.choices?.[0]?.message?.content;
    return content ? `Recent Literature:\n${content}` : '';
  } catch {
    return '';
  }
}

// ─── Typing cursor ────────────────────────────────────────────────────────────
function TypingCursor() {
  return (
    <span
      className="inline-block w-[2px] h-[14px] bg-[#F97316] ml-0.5 align-middle"
      style={{ animation: 'cursorBlink 1s step-end infinite' }}
    />
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ isLoading, hasResponse, isGrounding }: { isLoading: boolean; hasResponse: boolean; isGrounding: boolean }) {
  if (isGrounding) {
    return (
      <span className="flex items-center gap-1.5 text-[11px] text-purple-400 font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
        Fetching Literature…
      </span>
    );
  }
  if (isLoading) {
    return (
      <span className="flex items-center gap-1.5 text-[11px] text-[#F97316] font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] animate-pulse" />
        Clinical Analysis…
      </span>
    );
  }
  if (hasResponse) {
    return (
      <span className="flex items-center gap-1.5 text-[11px] text-emerald-400 font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        Evidence-Based Report Ready
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-[11px] text-zinc-500 font-medium">
      <span className="w-1.5 h-1.5 rounded-full bg-zinc-600" />
      Awaiting Analysis
    </span>
  );
}

// ─── Clinical Norm Indicator ──────────────────────────────────────────────────
function NormBadge({ status }: { status: string }) {
  const config: Record<string, { color: string; bg: string }> = {
    'WITHIN NORM': { color: '#34D399', bg: 'rgba(52,211,153,0.1)' },
    'MILD DEVIATION': { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
    'SIGNIFICANT DEVIATION': { color: '#F97316', bg: 'rgba(249,115,22,0.1)' },
    'CLINICAL ALERT': { color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
  };
  const c = config[status] || config['WITHIN NORM'];
  return (
    <span
      className="text-[9px] font-bold px-1.5 py-0.5 rounded tracking-wide"
      style={{ color: c.color, background: c.bg, border: `1px solid ${c.color}30` }}
    >
      {status}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AIAnalysisPanel() {
  const { metrics, status } = useSession();
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', true);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isGrounding, setIsGrounding] = useState(false);
  const [literatureContext, setLiteratureContext] = useState('');
  const [usePerplexity, setUsePerplexity] = useState(true);
  const autoRefreshRef = useRef(autoRefresh);
  const metricsRef = useRef(metrics);
  const autoIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { autoRefreshRef.current = autoRefresh; }, [autoRefresh]);
  useEffect(() => { metricsRef.current = metrics; }, [metrics]);

  useEffect(() => {
    if (error) toast.error('Clinical analysis failed: ' + error.message);
  }, [error]);

  const runAnalysis = useCallback(async () => {
    let litContext = '';
    if (usePerplexity) {
      setIsGrounding(true);
      litContext = await fetchLiteratureContext(metricsRef.current);
      setLiteratureContext(litContext);
      setIsGrounding(false);
    }
    const prompt = buildClinicalEEGPrompt(metricsRef.current, litContext);
    sendMessage(
      [
        { role: 'system', content: CLINICAL_SYSTEM_PROMPT },
        { role: 'user', content: prompt },
      ],
      { max_completion_tokens: 900 }
    );
    setLastAnalyzedAt(new Date());
  }, [sendMessage, usePerplexity]);

  useEffect(() => {
    if (autoRefresh) {
      autoIntervalRef.current = setInterval(() => {
        if (!isLoading && !isGrounding) runAnalysis();
      }, 90_000);
    } else {
      if (autoIntervalRef.current) clearInterval(autoIntervalRef.current);
    }
    return () => { if (autoIntervalRef.current) clearInterval(autoIntervalRef.current); };
  }, [autoRefresh, isLoading, isGrounding, runAnalysis]);

  // Compute derived ratios for the metrics bar
  const tbr = metrics.thetaPower / Math.max(metrics.betaPower, 0.1);
  const engagementIndex = metrics.betaPower / (metrics.alphaPower + metrics.thetaPower);
  const dar = metrics.deltaPower / Math.max(metrics.alphaPower, 0.1);

  const formattedTime = lastAnalyzedAt
    ? lastAnalyzedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <div
      className="rounded-2xl border border-[#1E3A5F]/60 overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, rgba(11,22,40,0.97) 0%, rgba(6,13,26,0.99) 100%)',
        boxShadow: '0 0 40px rgba(249,115,22,0.06), 0 8px 32px rgba(0,0,0,0.4)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 border-b border-[#1E3A5F]/50"
        style={{ background: 'rgba(15,30,53,0.6)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, rgba(249,115,22,0.2) 0%, rgba(30,58,95,0.3) 100%)',
              border: '1px solid rgba(249,115,22,0.3)',
              boxShadow: '0 0 12px rgba(249,115,22,0.15)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
              <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
            </svg>
          </div>
          <div>
            <h3 className="text-[13px] font-bold text-white tracking-wide">Clinical EEG Analysis</h3>
            <p className="text-[11px] text-[#4A7AB5]">GPT-4o · High Reasoning · QEEG Normative Standards</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <StatusBadge isLoading={isLoading} hasResponse={!!response} isGrounding={isGrounding} />

          {/* Perplexity toggle */}
          <button
            onClick={() => setUsePerplexity(v => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all ${
              usePerplexity
                ? 'bg-purple-500/15 border-purple-500/40 text-purple-300' :'bg-transparent border-[#1E3A5F]/60 text-zinc-500 hover:text-zinc-300'
            }`}
            title={usePerplexity ? 'Perplexity literature grounding ON' : 'Enable Perplexity literature grounding'}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            Lit
          </button>

          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all ${
              autoRefresh
                ? 'bg-[#F97316]/15 border-[#F97316]/40 text-[#F97316]'
                : 'bg-transparent border-[#1E3A5F]/60 text-zinc-500 hover:text-zinc-300 hover:border-[#1E3A5F]'
            }`}
            title={autoRefresh ? 'Disable auto-refresh (90s)' : 'Enable auto-refresh every 90s'}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            Auto
          </button>

          {/* Analyze button */}
          <button
            onClick={runAnalysis}
            disabled={isLoading || isGrounding || status === 'connecting'}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-[12px] font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: (isLoading || isGrounding)
                ? 'rgba(249,115,22,0.3)'
                : 'linear-gradient(135deg, #F97316 0%, #EA6C0A 100%)',
              boxShadow: (isLoading || isGrounding) ? 'none' : '0 2px 12px rgba(249,115,22,0.3)',
            }}
          >
            {isGrounding ? (
              <>
                <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" />
                </svg>
                Grounding…
              </>
            ) : isLoading ? (
              <>
                <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" />
                </svg>
                Analyzing…
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
                Analyze
              </>
            )}
          </button>
        </div>
      </div>

      {/* Clinical metrics bar */}
      <div className="flex items-center gap-0 border-b border-[#1E3A5F]/40 overflow-x-auto">
        {[
          {
            label: 'CLI', value: metrics.cognitiveLoadIndex.toFixed(0), unit: '',
            color: metrics.cognitiveLoadIndex > 85 ? '#EF4444' : metrics.cognitiveLoadIndex > 65 ? '#F97316' : '#34D399',
            norm: metrics.cognitiveLoadIndex > 85 ? 'CLINICAL ALERT' : metrics.cognitiveLoadIndex > 65 ? 'SIGNIFICANT DEVIATION' : 'WITHIN NORM',
          },
          {
            label: 'Attn', value: metrics.attentionScore.toFixed(0), unit: '',
            color: metrics.attentionScore > 60 ? '#34D399' : metrics.attentionScore > 40 ? '#F97316' : '#EF4444',
            norm: metrics.attentionScore < 40 ? 'SIGNIFICANT DEVIATION' : metrics.attentionScore < 60 ? 'MILD DEVIATION' : 'WITHIN NORM',
          },
          {
            label: 'TBR', value: tbr.toFixed(2), unit: '',
            color: tbr > 2.5 ? '#EF4444' : tbr > 2.0 ? '#F97316' : tbr > 1.8 ? '#F59E0B' : '#34D399',
            norm: tbr > 2.5 ? 'CLINICAL ALERT' : tbr > 2.0 ? 'SIGNIFICANT DEVIATION' : tbr > 1.8 ? 'MILD DEVIATION' : 'WITHIN NORM',
          },
          {
            label: 'TAR', value: metrics.thetaAlphaRatio.toFixed(2), unit: '',
            color: metrics.thetaAlphaRatio > 1.8 ? '#F97316' : '#A78BFA',
            norm: metrics.thetaAlphaRatio > 1.8 ? 'SIGNIFICANT DEVIATION' : 'WITHIN NORM',
          },
          {
            label: 'Coh', value: metrics.interHemisphericCoherence.toFixed(0), unit: '%',
            color: metrics.interHemisphericCoherence > 70 ? '#34D399' : metrics.interHemisphericCoherence > 40 ? '#F97316' : '#EF4444',
            norm: metrics.interHemisphericCoherence < 40 ? 'SIGNIFICANT DEVIATION' : metrics.interHemisphericCoherence < 70 ? 'MILD DEVIATION' : 'WITHIN NORM',
          },
          {
            label: 'EI', value: engagementIndex.toFixed(2), unit: '',
            color: engagementIndex > 0.4 ? '#34D399' : engagementIndex > 0.2 ? '#F59E0B' : '#EF4444',
            norm: engagementIndex < 0.2 ? 'SIGNIFICANT DEVIATION' : engagementIndex < 0.4 ? 'MILD DEVIATION' : 'WITHIN NORM',
          },
          { label: 'δ', value: metrics.deltaPower.toFixed(1), unit: 'µV²', color: '#60A5FA', norm: metrics.deltaPower > 25 ? 'MILD DEVIATION' : 'WITHIN NORM' },
          { label: 'θ', value: metrics.thetaPower.toFixed(1), unit: 'µV²', color: '#A78BFA', norm: metrics.thetaPower > 20 ? 'MILD DEVIATION' : 'WITHIN NORM' },
          { label: 'α', value: metrics.alphaPower.toFixed(1), unit: 'µV²', color: '#34D399', norm: metrics.alphaPower < 8 || metrics.alphaPower > 35 ? 'MILD DEVIATION' : 'WITHIN NORM' },
          { label: 'β', value: metrics.betaPower.toFixed(1), unit: 'µV²', color: '#F97316', norm: metrics.betaPower > 18 ? 'MILD DEVIATION' : 'WITHIN NORM' },
          { label: 'γ', value: metrics.gammaPower.toFixed(1), unit: 'µV²', color: '#F472B6', norm: metrics.gammaPower > 10 ? 'MILD DEVIATION' : 'WITHIN NORM' },
        ].map((m) => (
          <div
            key={m.label}
            className="flex flex-col items-center px-3 py-2 border-r border-[#1E3A5F]/30 flex-shrink-0 gap-0.5"
          >
            <span className="text-[10px] text-[#4A7AB5] font-medium tracking-wide">{m.label}</span>
            <span className="text-[13px] font-bold font-mono tabular-nums" style={{ color: m.color }}>
              {m.value}<span className="text-[10px] text-zinc-600 ml-0.5">{m.unit}</span>
            </span>
            <NormBadge status={m.norm} />
          </div>
        ))}
      </div>

      {/* Literature context strip */}
      {literatureContext && (
        <div
          className="px-5 py-2.5 border-b border-purple-500/20 text-[11px] text-purple-300/80 leading-relaxed"
          style={{ background: 'rgba(139,92,246,0.05)' }}
        >
          <span className="font-bold text-purple-400 mr-2">📚 Literature Grounding:</span>
          {literatureContext.replace('Recent Literature:\n', '')}
        </div>
      )}

      {/* Response area */}
      <div className="p-5 min-h-[200px]">
        {!response && !isLoading && !isGrounding && (
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.15)' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </div>
            <div className="text-center">
              <p className="text-[13px] text-zinc-400 font-medium">No clinical analysis yet</p>
              <p className="text-[12px] text-zinc-600 mt-1">
                Click <span className="text-[#F97316]">Analyze</span> for evidence-based QEEG interpretation grounded in published normative databases.
                {usePerplexity && <span className="text-purple-400"> Perplexity will fetch current literature first.</span>}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-1">
              {['NeuroGuide Norms', 'Klimesch IAF', 'Snyder TBR', 'Nunez Coherence', 'GPT-4o Analysis'].map(tag => (
                <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full border border-[#1E3A5F]/60 text-[#4A7AB5]">{tag}</span>
              ))}
            </div>
          </div>
        )}

        {isGrounding && !isLoading && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <div className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4 text-purple-400" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="rgba(139,92,246,0.3)" strokeWidth="3" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="#A78BFA" strokeWidth="3" strokeLinecap="round" />
              </svg>
              <span className="text-[12px] text-purple-300">Querying Perplexity for current EEG literature…</span>
            </div>
            <p className="text-[11px] text-zinc-600">Grounding analysis in 2022–2025 neuroscience research</p>
          </div>
        )}

        {(response || isLoading) && (
          <div className="prose prose-invert prose-sm max-w-none">
            <div className="text-[13px] leading-relaxed text-zinc-300" style={{ fontFamily: 'inherit' }}>
              <ReactMarkdown
                components={{
                  h2: ({ children }) => (
                    <h2 className="text-[13px] font-bold text-[#F97316] mt-4 mb-2 first:mt-0 tracking-wide">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-[12px] font-bold text-white mt-3 mb-1.5 tracking-wide">{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-[13px] text-zinc-300 leading-relaxed mb-2">{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className="space-y-1 mb-3 pl-0 list-none">{children}</ul>
                  ),
                  li: ({ children }) => (
                    <li className="flex items-start gap-2 text-[13px] text-zinc-300">
                      <span className="text-[#F97316] mt-0.5 flex-shrink-0">›</span>
                      <span>{children}</span>
                    </li>
                  ),
                  strong: ({ children }) => (
                    <strong className="text-white font-semibold">{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em className="text-[#8BAFD4] not-italic">{children}</em>
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto mb-3">
                      <table className="w-full text-[11px] border-collapse">{children}</table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th className="text-left px-2 py-1 text-[#4A7AB5] font-semibold border-b border-[#1E3A5F]/60">{children}</th>
                  ),
                  td: ({ children }) => (
                    <td className="px-2 py-1 text-zinc-300 border-b border-[#1E3A5F]/30">{children}</td>
                  ),
                }}
              >
                {response}
              </ReactMarkdown>
              {isLoading && <TypingCursor />}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {formattedTime && (
        <div className="px-5 py-3 border-t border-[#1E3A5F]/40 flex items-center justify-between">
          <span className="text-[11px] text-zinc-600">
            Last analyzed at <span className="text-zinc-500 font-mono">{formattedTime}</span>
            {usePerplexity && <span className="text-purple-400/60 ml-2">· Literature-grounded</span>}
          </span>
          <div className="flex items-center gap-3">
            {autoRefresh && (
              <span className="text-[11px] text-[#F97316]/60 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-[#F97316]/60 animate-pulse" />
                Auto · 90s
              </span>
            )}
            <span className="text-[10px] text-zinc-700">GPT-4o · QEEG norms · Evidence-based</span>
          </div>
        </div>
      )}

      <style>{`
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
